import classNames from "classnames";
import {
  IWebsiteBuilderElementBase,
  IWebsiteBuilderElementDataBase,
  TWebsiteBuilderElement,
} from "./websiteBuilderElement";
import { websiteBuilderNormalizeValue, websiteBuilderNormalizeHexColor } from "../../schema/normalization/websiteBuilderNormalizeValue";
import { IWebsiteBuilderNodeDesignStyles, TWebsiteBuilderNodeDatasetFromModel, TWebsiteBuilderNodeDatasetModelPrefixKeys } from "../websiteBuilderNode";
import { websiteBuilderGenerateDefaultHideableNodeVisibility } from "../websiteBuilderHideableNode";
import { TWebsiteBuilderDevice } from "../../website/shared/device";
import { clamp } from "lodash";
import { WB_BUTTON_BACKGROUND_COLOR_CSS_VARIABLE, WB_BUTTON_BORDER_COLOR_CSS_VARIABLE, WB_BUTTON_COLOR_CSS_VARIABLE, WB_BUTTON_FONT_SIZE_DESKTOP_CSS_VARIABLE, WB_BUTTON_FONT_SIZE_MOBILE_CSS_VARIABLE, WB_BUTTON_FONT_SIZE_TABLET_CSS_VARIABLE, WB_BUTTON_HOVER_BACKGROUND_COLOR_CSS_VARIABLE, WB_BUTTON_HOVER_BORDER_COLOR_CSS_VARIABLE, WB_BUTTON_HOVER_COLOR_CSS_VARIABLE } from "../../website/shared/cssVariables";
import { CSSProperties } from "react";
import { isWebsiteBuilderIconAssetIdentity, IWebsiteBuilderIconAssetIdentity } from "./websiteBuilderIcon";
import {
  IWebsiteBuilderLinkEmailDestination,
  IWebsiteBuilderLinkUrlDestination,
  TWebsiteBuilderOptionalLink,
  TWebsiteBuilderOptionalLinkType,
  websiteBuilderNormalizeOptionalLink,
} from "./websiteBuilderLink";

export const WEBSITE_BUILDER_BUTTON_MIN_FONT_SIZE = 8;
export const WEBSITE_BUILDER_BUTTON_MAX_FONT_SIZE = 48;
export const WEBSITE_BUILDER_BUTTON_MIN_BORDER_RADIUS = 0;
export const WEBSITE_BUILDER_BUTTON_MAX_BORDER_RADIUS = 100;

export type TWebsiteBuilderButtonType = "primary" | "secondary";

function isWebsiteBuilderButtonType(value: string): value is TWebsiteBuilderButtonType {
  return value === "primary" || value === "secondary";
}

export type TWebsiteBuilderButtonBorderStyle = "solid" | "transparent";

function isWebsiteBuilderButtonBorderStyle(value: string): value is TWebsiteBuilderButtonBorderStyle {
  return value === "solid" || value === "transparent";
}

export type TWebsiteBuilderButtonIconPosition = "left" | "right";
export type TWebsiteBuilderButtonIconHoverMotion = "none" | "fade";

export interface IWebsiteBuilderButtonIcon {
  assetIdentity: IWebsiteBuilderIconAssetIdentity;
  position: TWebsiteBuilderButtonIconPosition;
  hoverMotion: TWebsiteBuilderButtonIconHoverMotion;
}

function isWebsiteBuilderButtonIconPosition(value: string): value is TWebsiteBuilderButtonIconPosition {
  return value === "left" || value === "right";
}

function isWebsiteBuilderButtonIconHoverMotion(value: string): value is TWebsiteBuilderButtonIconHoverMotion {
  return value === "none" || value === "fade";
}

type TWebsiteBuilderButtonLinkDatasetModel =
  TWebsiteBuilderNodeDatasetModelPrefixKeys<
    { type: TWebsiteBuilderOptionalLinkType } &
    Omit<IWebsiteBuilderLinkUrlDestination, "type"> &
    Omit<IWebsiteBuilderLinkEmailDestination, "type">,
    "link"
  >;

export type TWebsiteBuilderButtonFontSize = {
  [K in TWebsiteBuilderDevice]: number;
};

export interface IWebsiteBuilderButtonSettings {
  type: TWebsiteBuilderButtonType;
  label: string;
  borderStyle: TWebsiteBuilderButtonBorderStyle;
  borderRadius: number;
  icon: IWebsiteBuilderButtonIcon;
  link: TWebsiteBuilderOptionalLink;
  fontSize: TWebsiteBuilderButtonFontSize;
  color: string;
  backgroundColor: string;
  borderColor: string;
  hoverColor: string;
  hoverBackgroundColor: string;
  hoverBorderColor: string;
}

export interface IWebsiteBuilderButtonData extends IWebsiteBuilderElementDataBase {
  type: "button";
  settings: IWebsiteBuilderButtonSettings;
}

export interface IWebsiteBuilderButton extends IWebsiteBuilderElementBase {
  element: IWebsiteBuilderButtonData;
}

type TWebsiteBuilderButtonDatasetModel = TWebsiteBuilderButtonLinkDatasetModel;
export type TWebsiteBuilderButtonDataset = TWebsiteBuilderNodeDatasetFromModel<TWebsiteBuilderButtonDatasetModel>;

export function isWebsiteBuilderButton(node: TWebsiteBuilderElement): node is IWebsiteBuilderButton {
  return node?.element?.type === "button";
}

function generateDefaultButtonSettings(): IWebsiteBuilderButtonSettings {
  return {
    type: "primary",
    label: "Action", // TODO: Translation
    borderStyle: "solid",
    borderRadius: WEBSITE_BUILDER_BUTTON_MIN_BORDER_RADIUS,
    icon: null,
    link: { type: "none" },
    color: null,
    fontSize: {
      desktop: null,
      tablet: null,
      mobile: null,
    },
    backgroundColor: null,
    borderColor: null,
    hoverColor: null,
    hoverBackgroundColor: null,
    hoverBorderColor: null,
  };
}

export function websiteBuilderGenerateDefaultButton(): IWebsiteBuilderButton {
  return {
    id: crypto.randomUUID(),
    type: "element",
    element: {
      type: "button",
      layout: {
        desktop: {
          position: { x: 0, y: 0 },
          dimensions: { width: 70, height: 35 },
        },
        tablet: {
          position: { x: 0, y: 0 },
          dimensions: { width: 70, height: 35 },
        },
        mobile: {
          position: { x: 0, y: 0 },
          dimensions: { width: 70, height: 35 },
        },
      },
      visibility: websiteBuilderGenerateDefaultHideableNodeVisibility(),
      zIndex: 0,
      settings: generateDefaultButtonSettings(),
    },
  };
}

export function websiteBuilderNormalizeButtonSettings(partialSettings: Partial<IWebsiteBuilderButtonSettings>): IWebsiteBuilderButtonSettings {
  const defaultSettings = generateDefaultButtonSettings();

  if (partialSettings == null) {
    return defaultSettings;
  }

  const {
    type,
    label,
    borderStyle,
    borderRadius,
    icon,
    link,
    fontSize,
    color,
    backgroundColor,
    borderColor,
    hoverColor,
    hoverBackgroundColor,
    hoverBorderColor,
  } = partialSettings;

  const normalizedFontSize: TWebsiteBuilderButtonFontSize = fontSize == null ? defaultSettings.fontSize : {
    desktop: websiteBuilderNormalizeValue({
      type: "number",
      value: fontSize.desktop,
      defaultValue: defaultSettings.fontSize.desktop,
      required: false,
      transform: (value: number) => clamp(value, WEBSITE_BUILDER_BUTTON_MIN_FONT_SIZE, WEBSITE_BUILDER_BUTTON_MAX_FONT_SIZE),
    }),
    tablet: websiteBuilderNormalizeValue({
      type: "number",
      value: fontSize.tablet,
      defaultValue: defaultSettings.fontSize.tablet,
      required: false,
      transform: (value: number) => clamp(value, WEBSITE_BUILDER_BUTTON_MIN_FONT_SIZE, WEBSITE_BUILDER_BUTTON_MAX_FONT_SIZE),
    }),
    mobile: websiteBuilderNormalizeValue({
      type: "number",
      value: fontSize.mobile,
      defaultValue: defaultSettings.fontSize.mobile,
      required: false,
      transform: (value: number) => clamp(value, WEBSITE_BUILDER_BUTTON_MIN_FONT_SIZE, WEBSITE_BUILDER_BUTTON_MAX_FONT_SIZE),
    }),
  };

  const normalizedButtonSettings: IWebsiteBuilderButtonSettings = {
    type: websiteBuilderNormalizeValue({
      type: "string",
      value: type,
      defaultValue: defaultSettings.type,
      required: true,
      validate: isWebsiteBuilderButtonType,
    }) as TWebsiteBuilderButtonType,
    label: websiteBuilderNormalizeValue({
      type: "string",
      value: label,
      defaultValue: defaultSettings.label,
      required: true,
    }),
    borderStyle: websiteBuilderNormalizeValue({
      type: "string",
      value: borderStyle,
      defaultValue: defaultSettings.borderStyle,
      required: true,
      validate: isWebsiteBuilderButtonBorderStyle,
    }) as TWebsiteBuilderButtonBorderStyle,
    borderRadius: websiteBuilderNormalizeValue({
      type: "number",
      value: borderRadius,
      defaultValue: defaultSettings.borderRadius,
      required: true,
      transform: (value: number) => clamp(
        value,
        WEBSITE_BUILDER_BUTTON_MIN_BORDER_RADIUS,
        WEBSITE_BUILDER_BUTTON_MAX_BORDER_RADIUS,
      ),
    }),
    icon: isWebsiteBuilderIconAssetIdentity(icon?.assetIdentity)
      ? {
          assetIdentity: {
            providerCode: icon.assetIdentity.providerCode,
            versionCode: icon.assetIdentity.versionCode,
            variantCode: icon.assetIdentity.variantCode,
            assetCode: icon.assetIdentity.assetCode,
          },
          position: websiteBuilderNormalizeValue({
            type: "string",
            value: icon.position,
            defaultValue: "left",
            required: true,
            validate: isWebsiteBuilderButtonIconPosition,
          }) as TWebsiteBuilderButtonIconPosition,
          hoverMotion: websiteBuilderNormalizeValue({
            type: "string",
            value: icon.hoverMotion,
            defaultValue: "none",
            required: true,
            validate: isWebsiteBuilderButtonIconHoverMotion,
          }) as TWebsiteBuilderButtonIconHoverMotion,
        }
      : defaultSettings.icon,
    link: websiteBuilderNormalizeOptionalLink(link),
    fontSize: normalizedFontSize,
    color: websiteBuilderNormalizeHexColor(color, defaultSettings.color, false),
    backgroundColor: websiteBuilderNormalizeHexColor(backgroundColor, defaultSettings.backgroundColor, false),
    borderColor: websiteBuilderNormalizeHexColor(borderColor, defaultSettings.borderColor, false),
    hoverColor: websiteBuilderNormalizeHexColor(hoverColor, defaultSettings.hoverColor, false),
    hoverBackgroundColor: websiteBuilderNormalizeHexColor(hoverBackgroundColor, defaultSettings.hoverBackgroundColor, false),
    hoverBorderColor: websiteBuilderNormalizeHexColor(hoverBorderColor, defaultSettings.hoverBorderColor, false),
  };

  return normalizedButtonSettings;
}

interface IWebsiteBuilderButtonDesignStyles extends IWebsiteBuilderNodeDesignStyles {
  contentClassName: string;
  labelClassName: string;
  iconClassName: string;
}

export function websiteBuilderGenerateButtonDesignStyles(
  settings: IWebsiteBuilderButtonSettings,
): IWebsiteBuilderButtonDesignStyles {
  const borderColor = settings.borderStyle === "transparent" ? "transparent" : settings.borderColor;

  const styleProperties: CSSProperties = {
    ...(settings.fontSize.desktop != null ? { [WB_BUTTON_FONT_SIZE_DESKTOP_CSS_VARIABLE]: `${settings.fontSize.desktop}px` } : {}),
    ...(settings.fontSize.tablet != null ? { [WB_BUTTON_FONT_SIZE_TABLET_CSS_VARIABLE]: `${settings.fontSize.tablet}px` } : {}),
    ...(settings.fontSize.mobile != null ? { [WB_BUTTON_FONT_SIZE_MOBILE_CSS_VARIABLE]: `${settings.fontSize.mobile}px` } : {}),
    ...(settings.color != null ? { [WB_BUTTON_COLOR_CSS_VARIABLE]: settings.color } : {}),
    ...(settings.backgroundColor != null ? { [WB_BUTTON_BACKGROUND_COLOR_CSS_VARIABLE]: settings.backgroundColor } : {}),
    ...(borderColor != null ? { [WB_BUTTON_BORDER_COLOR_CSS_VARIABLE]: borderColor } : {}),
    ...(settings.hoverColor != null ? { [WB_BUTTON_HOVER_COLOR_CSS_VARIABLE]: settings.hoverColor } : {}),
    ...(settings.hoverBackgroundColor != null ? { [WB_BUTTON_HOVER_BACKGROUND_COLOR_CSS_VARIABLE]: settings.hoverBackgroundColor } : {}),
    ...(settings.hoverBorderColor != null ? { [WB_BUTTON_HOVER_BORDER_COLOR_CSS_VARIABLE]: settings.hoverBorderColor } : {}),
    borderRadius: settings.borderRadius,
  };

  let styleDeclaration = "";

  if (settings.fontSize.desktop != null) {
    styleDeclaration += `${WB_BUTTON_FONT_SIZE_DESKTOP_CSS_VARIABLE}: ${settings.fontSize.desktop}px;`;
  }
  if (settings.fontSize.tablet != null) {
    styleDeclaration += `${WB_BUTTON_FONT_SIZE_TABLET_CSS_VARIABLE}: ${settings.fontSize.tablet}px;`;
  }
  if (settings.fontSize.mobile != null) {
    styleDeclaration += `${WB_BUTTON_FONT_SIZE_MOBILE_CSS_VARIABLE}: ${settings.fontSize.mobile}px;`;
  }
  if (settings.color != null) {
    styleDeclaration += `${WB_BUTTON_COLOR_CSS_VARIABLE}: ${settings.color};`;
  }
  if (settings.backgroundColor != null) {
    styleDeclaration += `${WB_BUTTON_BACKGROUND_COLOR_CSS_VARIABLE}: ${settings.backgroundColor};`;
  }
  if (borderColor != null) {
    styleDeclaration += `${WB_BUTTON_BORDER_COLOR_CSS_VARIABLE}: ${borderColor};`;
  }
  if (settings.hoverColor != null) {
    styleDeclaration += `${WB_BUTTON_HOVER_COLOR_CSS_VARIABLE}: ${settings.hoverColor};`;
  }
  if (settings.hoverBackgroundColor != null) {
    styleDeclaration += `${WB_BUTTON_HOVER_BACKGROUND_COLOR_CSS_VARIABLE}: ${settings.hoverBackgroundColor};`;
  }
  if (settings.hoverBorderColor != null) {
    styleDeclaration += `${WB_BUTTON_HOVER_BORDER_COLOR_CSS_VARIABLE}: ${settings.hoverBorderColor};`;
  }

  if (styleProperties.borderRadius != null) {
    styleDeclaration += `border-radius: ${styleProperties.borderRadius}px;`;
  }

  return {
    className: classNames("website-builder-button", {
      [`button-${settings.type}`]: settings.type,
      "button-icon-fade": settings.icon?.hoverMotion === "fade",
      [`button-icon-${settings.icon?.position}`]: settings.icon?.hoverMotion === "fade",
    }),
    contentClassName: "website-builder-button-content",
    labelClassName: "website-builder-button-label",
    iconClassName: "website-builder-button-icon",
    styleProperties,
    styleDeclaration,
  };
}
